/**
 * DOCX export-only (html-docx-js): embed images and size inner GHG report media.
 */

import {
  getGhgCoverLogoWordInlineStyle,
  GHG_WORD_EXPORT_DESIGN_TOKENS,
} from './ghgWordExportDesign';

/** Printable width A4 landscape with 0.5in margins each side */
export const DOCX_CONTENT_WIDTH_IN = 10.69;
export const DOCX_CONTENT_WIDTH_PX = Math.round(DOCX_CONTENT_WIDTH_IN * 96);

/** Inner GHG report images (not cover / TOC) */
export const GHG_INNER_IMAGE_WIDTH_PERCENT = 0.55;

export const WORD_DOCX_GHG_INNER_IMAGE_CSS = `
body section.ghg-section img.word-docx-ghg-inner-img,
body .Main_Section.ghg-section img.word-docx-ghg-inner-img,
body img.word-docx-ghg-inner-img {
  width: 55% !important;
  max-width: 55% !important;
  height: auto !important;
  display: block !important;
  margin-left: auto !important;
  margin-right: auto !important;
}
body #Section_Cover img,
body section.GHGSection_cover img,
body .ghg-cover-page img,
body .ghg-word-export-page img,
body .word-cover-page img,
body .word-cover-content img,
body .ghg-word-toc img,
body .ghg-section-head-pill-img img,
body img.ghg-section-head-pill-img {
  width: auto !important;
  max-width: none !important;
  height: auto !important;
  display: inline-block !important;
  margin: 0 !important;
}
body img.ghg-cover-logo,
body #Section_Cover .report-company-logo img,
body section.GHGSection_cover .report-company-logo img {
  height: ${GHG_WORD_EXPORT_DESIGN_TOKENS.coverLogoHeightPt} !important;
  max-height: ${GHG_WORD_EXPORT_DESIGN_TOKENS.coverLogoHeightPt} !important;
  width: auto !important;
  max-width: ${GHG_WORD_EXPORT_DESIGN_TOKENS.coverLogoMaxWidthPt} !important;
  display: inline-block !important;
  margin: 0 !important;
}
`.trim();

const DECORATIVE_IMG_MARKERS = [
  'Report_image_S.webp',
  'Report_image1.webp',
  'Report_image3.webp',
  'Report_image_E.webp',
  'Report_image_G.webp',
  'Cover-image-1-1280x720.jpg',
];

const COVER_SKIP_SELECTOR =
  '#Section_Cover, section.GHGSection_cover, .ghg-cover-page, .ghg-word-export-page, .word-cover-page, .word-cover-content, .ghg-word-toc';

const PX_PER_PT = 96 / 72;

const parsePtToPx = (ptValue) => {
  const n = parseFloat(String(ptValue || '0'));
  return Number.isFinite(n) ? n * PX_PER_PT : 0;
};

const mergeInlineStyle = (el, addition) => {
  if (!el?.setAttribute) return;
  let cur = (el.getAttribute('style') || '').trim();
  if (cur && !cur.endsWith(';')) cur += ';';
  el.setAttribute('style', `${cur} ${addition}`.trim());
};

const isDecorativeExportImage = (src) =>
  DECORATIVE_IMG_MARKERS.some((marker) => String(src || '').includes(marker));

/** Company logo on GHG cover (Word export sizing only). */
export const isGhgCoverLogoImage = (img) => {
  if (!img?.closest) return false;
  const cls = img.getAttribute('class') || '';
  if (/ghg-cover-logo/i.test(cls)) return true;
  if (img.classList.contains('ghg-cover-meta-icon-img')) return false;
  if (img.closest('.ghg-cover-label-badge-img, .ghg-cover-label-badge, .ghg-toc-label-badge-img')) {
    return false;
  }
  if (img.closest('.report-company-logo')) return true;
  const cover = img.closest(
    '#Section_Cover, section.GHGSection_cover, .ghg-cover-page, .ghg-word-export-page, .ghg-word-cover-export',
  );
  if (!cover) return false;
  const row = img.closest('tr');
  if (row && row.parentElement?.rows?.[0] === row) return true;
  return false;
};

/** Cover page + TOC badges/logos — skip inner 55% sizing (cover logo handled separately). */
export const isGhgCoverOrTocImage = (img) => {
  if (!img?.closest) return false;
  if (isGhgCoverLogoImage(img)) return false;
  if (img.closest(COVER_SKIP_SELECTOR)) return true;
  const cls = img.getAttribute('class') || '';
  if (/ghg-section-head-pill-img/i.test(cls)) return true;
  if (img.closest('.ghg-section-head-pill-img')) return true;
  return /ghg-toc-number-badge|ghg-cover-meta-icon|ghg-toc-label-badge|ghg-cover-label-badge/i.test(cls);
};

/** GHG cover company logo — explicit pt width/height for html-docx-js. */
export const applyGhgCoverLogoSizingForWord = (img, naturalW, naturalH) => {
  if (!img?.setAttribute) return;

  const targetH = parsePtToPx(GHG_WORD_EXPORT_DESIGN_TOKENS.coverLogoHeightPt);
  const maxW = parsePtToPx(GHG_WORD_EXPORT_DESIGN_TOKENS.coverLogoMaxWidthPt);
  const nw = Math.max(1, Number(naturalW) || maxW);
  const nh = Math.max(1, Number(naturalH) || targetH);
  let widthPx = Math.round(nw * (targetH / nh));
  widthPx = Math.min(widthPx, Math.round(maxW));
  const heightPx = Math.round(targetH);

  const widthPt = Math.round(((widthPx * 72) / 96) * 10) / 10;
  const heightPt = Math.round(((heightPx * 72) / 96) * 10) / 10;

  const cls = (img.getAttribute('class') || '').split(/\s+/).filter(Boolean);
  if (!cls.includes('ghg-cover-logo')) cls.push('ghg-cover-logo');
  img.setAttribute('class', cls.join(' '));
  img.setAttribute('width', String(widthPt));
  img.setAttribute('height', String(heightPt));
  mergeInlineStyle(img, getGhgCoverLogoWordInlineStyle());
};

const measureImagePx = (src) =>
  new Promise((resolve) => {
    if (!src) {
      resolve({ w: DOCX_CONTENT_WIDTH_PX, h: Math.round(DOCX_CONTENT_WIDTH_PX * 0.5) });
      return;
    }
    const probe = new Image();
    probe.crossOrigin = 'Anonymous';
    probe.onerror = () => resolve({ w: DOCX_CONTENT_WIDTH_PX, h: Math.round(DOCX_CONTENT_WIDTH_PX * 0.5) });
    probe.onload = () => {
      resolve({
        w: probe.naturalWidth || probe.width || DOCX_CONTENT_WIDTH_PX,
        h: probe.naturalHeight || probe.height || Math.round(DOCX_CONTENT_WIDTH_PX * 0.5),
      });
    };
    probe.src = src;
  });

export const computeGhgInnerImageDimensions = (naturalW, naturalH, widthPercent = GHG_INNER_IMAGE_WIDTH_PERCENT) => {
  const nw = Math.max(1, Number(naturalW) || DOCX_CONTENT_WIDTH_PX);
  const nh = Math.max(1, Number(naturalH) || Math.round(nw * 0.5));
  const targetW = Math.max(1, Math.round(DOCX_CONTENT_WIDTH_PX * widthPercent));
  const targetH = Math.max(1, Math.round(nh * (targetW / nw)));
  return { widthPx: targetW, heightPx: targetH };
};

/** Inner GHG images: 55% page content width, height auto (via proportional height attr). */
export const applyGhgInnerImageSizingForWord = (img, naturalW, naturalH) => {
  if (!img?.setAttribute || isGhgCoverOrTocImage(img)) return;

  const { widthPx, heightPx } = computeGhgInnerImageDimensions(naturalW, naturalH);
  const cls = (img.getAttribute('class') || '').split(/\s+/).filter(Boolean);
  if (!cls.includes('word-docx-ghg-inner-img')) cls.push('word-docx-ghg-inner-img');
  img.setAttribute('class', cls.join(' '));

  img.setAttribute('width', String(widthPx));
  img.setAttribute('height', String(heightPx));
  mergeInlineStyle(
    img,
    `display:block;width:${widthPx}px;max-width:55%;height:${heightPx}px;margin-left:auto;margin-right:auto;border:0;`,
  );
};

const loadImageAsDataUrl = (url) =>
  new Promise((resolve) => {
    if (!url || /^data:image\//i.test(url)) {
      resolve(url || null);
      return;
    }
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onerror = () => resolve(null);
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        if (!canvas.width || !canvas.height) {
          resolve(null);
          return;
        }
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch {
        resolve(null);
      }
    };
    img.src = url;
  });

const processImagesInRoot = async (root, options = {}) => {
  const applyGhgSizing = options.ghgInnerImages === true;
  const images = [...(root?.querySelectorAll?.('img') || [])];

  await Promise.all(
    images.map(async (img) => {
      const src = img.getAttribute('src') || img.src || '';
      if (isDecorativeExportImage(src)) {
        img.remove();
        return;
      }

      let finalSrc = src;
      if (!/^data:image\//i.test(src)) {
        const base64 = await loadImageAsDataUrl(src);
        if (base64) {
          finalSrc = base64;
          img.setAttribute('src', base64);
        }
      }

      if (applyGhgSizing && isGhgCoverLogoImage(img)) {
        const { w, h } = await measureImagePx(finalSrc);
        applyGhgCoverLogoSizingForWord(img, w, h);
        return;
      }

      if (isGhgCoverOrTocImage(img)) return;

      if (applyGhgSizing) {
        const { w, h } = await measureImagePx(finalSrc);
        applyGhgInnerImageSizingForWord(img, w, h);
      }
    }),
  );
};

const getExportRootFromHtml = (htmlString) => {
  const isFullDoc = /<html[\s>]/i.test(htmlString);
  const doc = new DOMParser().parseFromString(
    isFullDoc ? htmlString : `<div id="docx-export-root">${htmlString}</div>`,
    'text/html',
  );
  const root = isFullDoc ? doc.body : doc.getElementById('docx-export-root');
  return { doc, root, isFullDoc };
};

const serializeExportRoot = ({ doc, root, isFullDoc }) =>
  (isFullDoc ? doc.documentElement.outerHTML : root?.innerHTML || '');

/**
 * Embed images as base64; optionally size inner GHG images to 55% width.
 * @param {string} htmlString
 * @param {(html: string) => void} callback
 * @param {{ ghgInnerImages?: boolean }} [options]
 */
export async function prepareHtmlForDocxExport(htmlString, callback, options = {}) {
  if (!htmlString || typeof DOMParser === 'undefined') {
    callback(htmlString || '');
    return;
  }

  const { doc, root, isFullDoc } = getExportRootFromHtml(htmlString);
  if (!root) {
    callback(htmlString);
    return;
  }

  await processImagesInRoot(root, options);
  callback(serializeExportRoot({ doc, root, isFullDoc }));
}
